/**
 * Chutt... - Reputation Intelligence Platform
 * Luxury Ivoire & Emeraude — Refonte UX 2026
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Pages
import Login from './pages/Login';
import CrisisPage from './pages/CrisisPage';
import HistoriqueReportPage from './pages/HistoriqueReportPage';
import SettingsPage from './pages/SettingsPage';

// V2 Pages
import RadarPage from './pages/RadarPage';
import ComprendrePage from './pages/ComprendrePage';
import AgirPage from './pages/AgirPage';
import MesurerPage from './pages/MesurerPage';
import CockpitPage from './pages/CockpitPage';
import SuperAdminPage from './pages/SuperAdminPage';

// Layout
import { MainLayout } from './components/layout';

// Design system & Motion
import { colors } from './styles/colors';
import { MotionConfig } from 'framer-motion';
import { SnackbarProvider } from './components/shared/SnackbarProvider';

// Create Chutt... Luxury theme
const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary,
      light: colors.primaryLight,
      dark: colors.primaryDark,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.charcoal,
      light: '#2D3532',
      dark: '#111513',
      contrastText: colors.white,
    },
    success: {
      main: colors.success,
      light: '#4ADE80',
      dark: '#16A34A',
    },
    warning: {
      main: colors.warning,
      light: '#FBBF24',
      dark: '#D97706',
    },
    error: {
      main: colors.danger,
      light: '#F87171',
      dark: '#DC2626',
    },
    info: {
      main: colors.info,
      light: '#60A5FA',
      dark: '#2563EB',
    },
    background: {
      default: colors.ivory,
      paper: colors.white,
    },
    text: {
      primary: colors.charcoal,
      secondary: colors.textMed,
      disabled: colors.textLight,
    },
    divider: colors.borderLight,
  },
  typography: {
    fontFamily: [
      '"DM Sans"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontFamily: '"Playfair Display", Georgia, serif',
      fontWeight: 600,
      color: colors.charcoal,
      textWrap: 'balance',
    },
    h2: {
      fontFamily: '"Playfair Display", Georgia, serif',
      fontWeight: 600,
      color: colors.charcoal,
      textWrap: 'balance',
    },
    h3: {
      fontFamily: '"Playfair Display", Georgia, serif',
      fontWeight: 600,
      color: colors.charcoal,
      textWrap: 'balance',
    },
    h4: {
      fontFamily: '"Playfair Display", Georgia, serif',
      fontWeight: 500,
      color: colors.charcoal,
      textWrap: 'balance',
    },
    h5: {
      fontFamily: '"Playfair Display", Georgia, serif',
      fontWeight: 500,
      color: colors.charcoal,
    },
    h6: {
      fontFamily: '"Playfair Display", Georgia, serif',
      fontWeight: 500,
      color: colors.charcoal,
    },
    subtitle1: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 500,
    },
    subtitle2: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 600,
      fontSize: '0.75rem',
      letterSpacing: '0.04em',
    },
    body1: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    button: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 500,
      textTransform: 'none',
    },
    caption: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 400,
      color: colors.textLight,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(0, 0, 0, 0.03)',
    '0 1px 3px rgba(0, 0, 0, 0.04)',
    '0 2px 4px rgba(0, 0, 0, 0.04)',
    '0 4px 6px rgba(0, 0, 0, 0.04)',
    '0 6px 8px rgba(0, 0, 0, 0.06)',
    '0 8px 12px rgba(0, 0, 0, 0.06)',
    '0 10px 15px rgba(0, 0, 0, 0.06)',
    '0 12px 20px rgba(0, 0, 0, 0.08)',
    '0 16px 24px rgba(0, 0, 0, 0.08)',
    '0 20px 32px rgba(0, 0, 0, 0.10)',
    '0 24px 40px rgba(0, 0, 0, 0.10)',
    ...Array(13).fill('0 24px 40px rgba(0, 0, 0, 0.10)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: `${colors.border} ${colors.ivoryWarm}`,
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: colors.ivoryWarm,
          },
          '&::-webkit-scrollbar-thumb': {
            background: colors.border,
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: colors.textLight,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(12px)',
          background: 'rgba(255,255,255,0.65)',
          border: `1px solid ${colors.borderLight}`,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
        },
        elevation0: {
          boxShadow: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
        },
        elevation2: {
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: `1px solid ${colors.borderLight}`,
          borderRadius: '16px',
          backdropFilter: 'blur(12px)',
          background: 'rgba(255,255,255,0.65)',
          transition: 'box-shadow 0.3s ease, border-color 0.3s ease, transform 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(27, 94, 64, 0.08)',
            borderColor: colors.border,
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: '"DM Sans", sans-serif',
          fontWeight: 500,
          borderRadius: '100px',
          padding: '8px 20px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(27, 94, 64, 0.2)',
          },
        },
        containedPrimary: {
          backgroundColor: colors.primary,
          color: colors.white,
          '&:hover': {
            backgroundColor: colors.primaryDark,
          },
        },
        containedSecondary: {
          backgroundColor: colors.charcoal,
          '&:hover': {
            backgroundColor: '#2D3532',
          },
        },
        outlined: {
          borderColor: colors.border,
          color: colors.text,
          '&:hover': {
            borderColor: colors.primary,
            backgroundColor: 'rgba(27, 94, 64, 0.04)',
          },
        },
        outlinedPrimary: {
          borderColor: colors.primaryLight,
          color: colors.primary,
          '&:hover': {
            borderColor: colors.primary,
            backgroundColor: 'rgba(27, 94, 64, 0.04)',
          },
        },
        text: {
          color: colors.primary,
          '&:hover': {
            backgroundColor: 'rgba(27, 94, 64, 0.04)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"DM Sans", sans-serif',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderRadius: '100px',
        },
        colorPrimary: {
          backgroundColor: 'rgba(27, 94, 64, 0.08)',
          color: colors.primary,
        },
        colorSecondary: {
          backgroundColor: 'rgba(27, 94, 64, 0.05)',
          color: colors.primary,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          backgroundColor: colors.borderLight,
          height: 6,
        },
        barColorPrimary: {
          backgroundColor: colors.primary,
          borderRadius: '4px',
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: colors.primary,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.charcoal,
          color: colors.white,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(27, 94, 64, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(27, 94, 64, 0.18)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
        },
        indicator: {
          display: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: '"DM Sans", sans-serif',
          fontWeight: 500,
          minHeight: 36,
          borderRadius: '100px',
          padding: '6px 16px',
          color: colors.textMed,
          transition: 'all 0.2s ease',
          '&.Mui-selected': {
            color: colors.white,
            backgroundColor: colors.primary,
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: colors.ivoryWarm,
            fontFamily: '"DM Sans", sans-serif',
            fontWeight: 600,
            color: colors.textMed,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: `1px solid ${colors.borderLight}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontVariantNumeric: 'tabular-nums',
          borderBottom: `1px solid ${colors.borderLight}`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.charcoal,
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '0.75rem',
          fontWeight: 400,
          padding: '6px 12px',
          borderRadius: '8px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
        standardInfo: {
          backgroundColor: `${colors.info}10`,
          color: colors.charcoal,
        },
        standardSuccess: {
          backgroundColor: `${colors.success}10`,
          color: colors.charcoal,
        },
        standardWarning: {
          backgroundColor: `${colors.warning}10`,
          color: colors.charcoal,
        },
        standardError: {
          backgroundColor: `${colors.danger}10`,
          color: colors.charcoal,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            '&.Mui-focused fieldset': {
              borderColor: colors.primary,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: colors.primary,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '12px !important',
          border: `1px solid ${colors.borderLight}`,
          boxShadow: 'none',
          backdropFilter: 'blur(12px)',
          background: 'rgba(255,255,255,0.65)',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: '0 0 8px 0' },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: '"DM Sans", sans-serif',
          fontWeight: 500,
          borderColor: colors.borderLight,
          borderRadius: '100px !important',
          '&.Mui-selected': {
            backgroundColor: colors.primary,
            color: colors.white,
            borderColor: colors.primary,
            '&:hover': {
              backgroundColor: colors.primaryDark,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
      },
    },
  },
});

// Protected Route wrapper
function ProtectedRoute({ children, isAuthenticated }) {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = React.useState(
    localStorage.getItem('token') !== null
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MotionConfig reducedMotion="user">
      <SnackbarProvider>
      <Router>
        <Routes>
          {/* Public route: Login */}
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/radar" replace />
              ) : (
                <Login setAuth={setIsAuthenticated} />
              )
            }
          />

          {/* Protected routes with MainLayout */}
          <Route
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <MainLayout setAuth={setIsAuthenticated} />
              </ProtectedRoute>
            }
          >
            {/* V2 Routes */}
            <Route path="/radar" element={<RadarPage />} />
            <Route path="/comprendre" element={<ComprendrePage />} />
            <Route path="/agir" element={<AgirPage />} />
            <Route path="/mesurer" element={<MesurerPage />} />
            <Route path="/dashboard-agence" element={<CockpitPage />} />

            {/* Remaining routes */}
            <Route path="/crisis" element={<CrisisPage />} />
            <Route path="/historique" element={<HistoriqueReportPage />} />
            <Route path="/report" element={<Navigate to="/historique" replace />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/superadmin" element={<SuperAdminPage />} />
          </Route>

          {/* Redirect root to Radar (V2 landing) */}
          <Route path="/" element={<Navigate to="/radar" replace />} />

          {/* Catch all - redirect to Radar */}
          <Route path="*" element={<Navigate to="/radar" replace />} />
        </Routes>
      </Router>
    </SnackbarProvider>
    </MotionConfig>
    </ThemeProvider>
  );
}

export default App;
